import type { NoteSection } from '@features/notes';

import type { NoteSectionType } from '../types/types';

export interface FindReplacePreview {
  sectionName: string;
  original: string;
  replaced: string;
  originalParts?: Array<{ text: string; isMatch: boolean }>;
  replacedParts?: Array<{ text: string; isMatch: boolean }>;
}

const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const findReplaceInSections = (
  sections: NoteSectionType[] | NoteSection[],
  find: string,
  replace: string,
  caseSensitive = false,
  maxPreviews = 100, // Limit previews for performance
) => {
  if (!find) return { updatedSections: sections, previews: [] as FindReplacePreview[] };

  const flags = caseSensitive ? 'g' : 'gi';
  const previews: FindReplacePreview[] = [];
  let previewCount = 0;

  const updatedSections = sections.map(section => {
    const baseText = section.edited_text || section.text;
    const lines = baseText.split('\n');
    let changed = false;

    const newLines = lines.map(line => {
      // Create a fresh regex for each line to reset lastIndex
      const lineRegex = new RegExp(escapeRegExp(find), flags);
      const replacedLine = line.replace(lineRegex, replace);

      if (replacedLine !== line && previewCount < maxPreviews) {
        // Use matchAll for cleaner iteration (ES2020)
        const matches = Array.from(line.matchAll(new RegExp(escapeRegExp(find), flags)));

        if (matches.length > 0) {
          // Build original parts
          const originalParts: Array<{ text: string; isMatch: boolean }> = [];
          let lastIndex = 0;

          for (const match of matches) {
            const matchIndex = match.index;
            if (matchIndex > lastIndex) {
              originalParts.push({ text: line.substring(lastIndex, matchIndex), isMatch: false });
            }
            originalParts.push({ text: match[0], isMatch: true });
            lastIndex = matchIndex + match[0].length;
          }
          if (lastIndex < line.length) {
            originalParts.push({ text: line.substring(lastIndex), isMatch: false });
          }

          // Build replaced parts (reuse match positions)
          const replacedParts: Array<{ text: string; isMatch: boolean }> = [];
          lastIndex = 0;

          for (const match of matches) {
            const matchIndex = match.index;
            if (matchIndex > lastIndex) {
              replacedParts.push({ text: line.substring(lastIndex, matchIndex), isMatch: false });
            }
            replacedParts.push({ text: replace, isMatch: true });
            lastIndex = matchIndex + match[0].length;
          }
          if (lastIndex < line.length) {
            replacedParts.push({ text: line.substring(lastIndex), isMatch: false });
          }

          previews.push({
            sectionName: section.name,
            original: line,
            replaced: replacedLine,
            originalParts,
            replacedParts,
          });
          previewCount++;
        }
        changed = true;
      } else if (replacedLine !== line) {
        // Still mark as changed even if we hit preview limit
        changed = true;
      }

      return replacedLine;
    });

    // Always return a new object to avoid issues with frozen/non-extensible objects from state
    if (changed) {
      return { ...section, edited_text: newLines.join('\n') };
    } else {
      // Even if unchanged, return a new object to prevent mutation issues
      return { ...section };
    }
  });

  return { updatedSections, previews };
};

export default findReplaceInSections;
